import { Button, Radio, RadioChangeEvent, notification } from 'antd';
import { get, isEmpty, isEqual } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Editor, { DiffEditor } from '@monaco-editor/react';

export interface IJsonEditorType {
  data: any;
  refetch: () => void;
  handleSave: (payload) => void;
  height: string;
  index?: any;
  showDelete?: boolean;
  onDeleteConfig?: (payload) => void;
  onDiscardClick?: (value) => void;
}

const JSON_SPACE = '  ';
const EDITOR_OPTIONS = [
  { label: 'EDITOR', value: 'Editor' },
  { label: 'DIFF EDITOR', value: 'Diff Editor' },
];

const isEditor = (value: string) => {
  return isEqual(value, 'Editor');
};
const checkConfigurationVersion = (originalData, modifiedData) => {
  // if new config then do not check
  if (isEmpty(originalData)) return true;

  const equal = isEqual(
    get(originalData, 'configurationVersion'),
    get(modifiedData, 'configurationVersion'),
  );
  if (!equal) {
    notification.error({
      message: 'Verification Error',
      description: 'ConfigurationVersion cannot be modified!',
    });
  }
  return equal;
};

const JsonEditor = ({
  data,
  refetch,
  handleSave,
  height,
  showDelete = false,
  onDeleteConfig,
}: IJsonEditorType) => {
  const [api, contextHolder] = notification.useNotification();

  const editorRef = useRef<any>(null);
  const diffEditorRef = useRef<any>(null);

  const [editorMode, setEditorMode] = useState<string>('Editor');
  const [editorValue, setEditorValue] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<any[]>([]);

  const originalJsonData = useMemo(() => JSON.stringify(data ?? {}, null, JSON_SPACE), [data]);

  const initializeEditorValue = () => {
    setEditorValue(JSON.stringify(data ?? {}, null, JSON_SPACE));
  };

  useEffect(() => {
    initializeEditorValue();
  }, [data]);

  const openNotificationWithIcon = (type = 'error') => {
    api[type]({
      message: 'Verification Error',
      description: (
        <div>
          <ul>
            {validationErrors.map((error, index) => (
              <li key={index}>
                Line {get(error, 'startLineNumber')}: {get(error, 'message')}
              </li>
            ))}
          </ul>
        </div>
      ),
    });
  };

  function handleEditorChange(value, event) {
    setEditorValue(value);
  }

  function handleEditorDidMount(editor, monaco) {
    editorRef.current = editor;
  }

  function handleDiffEditorDidMount(editor) {
    diffEditorRef.current = editor;
  }

  function handleEditorValidation(markers) {
    setValidationErrors(markers);
  }

  const handleSaveRawConfig = () => {
    if (validationErrors.length !== 0) {
      openNotificationWithIcon();
      return;
    }
    // try catch to prevent fast operations, and validationErrors have not been generated.
    try {
      const value = editorRef.current?.getValue();
      const parsedValue = JSON.parse(value);

      const isEqualConfigurationVersion = checkConfigurationVersion(data, parsedValue);
      if (!isEqualConfigurationVersion) return;

      handleSave(parsedValue);
    } catch (error) {
      notification.error({
        message: 'Verification Error',
        description: get(error, 'message'),
      });
    }
  };

  const handleRefreshRawConfig = () => {
    initializeEditorValue();
    refetch();
  };

  const handleChangeEditorMode = ({ target: { value } }: RadioChangeEvent) => {
    if (isEditor(value)) {
      const value = diffEditorRef.current?.getModifiedEditor()?.getValue();
      setEditorValue(value);
    }
    setEditorMode(value);
  };

  return (
    <div style={{ padding: '0 16px' }}>
      {contextHolder}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          padding: '10px 0',
          justifyContent: 'space-between',
        }}
      >
        <Radio.Group
          options={EDITOR_OPTIONS}
          onChange={handleChangeEditorMode}
          value={editorMode}
          optionType="button"
        />
        {!isEditor(editorMode) ? null : (
          <div style={{ display: 'flex', gap: '5px' }}>
            <Button onClick={handleRefreshRawConfig}>Refresh</Button>
            <Button type="primary" onClick={handleSaveRawConfig}>
              Save
            </Button>
            {showDelete && (
              <Button danger onClick={() => onDeleteConfig && onDeleteConfig(data)}>
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
      <div style={{ borderStyle: 'ridge' }}>
        {!isEditor(editorMode) ? (
          <DiffEditor
            height={height}
            language="json"
            original={originalJsonData}
            modified={editorValue}
            onMount={handleDiffEditorDidMount}
          />
        ) : (
          <Editor
            height={height}
            defaultLanguage="json"
            value={editorValue}
            onChange={handleEditorChange}
            onMount={handleEditorDidMount}
            onValidate={handleEditorValidation}
          />
        )}
      </div>
    </div>
  );
};

export default JsonEditor;
